import React from 'react';
import { USER_ROLES, NAVIGATION_LINKS_DATA, fileTypes, DATA_CATEGORY_LABELS } from '../../constants';
import { useLocation } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import CustomDivider from '../reusable/CustomDivider';
import OverviewIcon from '../assets/nav-overview.svg';
import DashboardIcon from '../assets/nav-dashboard.svg';
import DataUploadIcon from '../assets/nav-data-upload.svg';
import DataQcIcon from '../assets/nav-data-qc.svg';
import CourseMappingIcon from '../assets/nav-course-mapping.svg';
import HelpIcon from '../assets/nav-help.svg';
import TranscriptsIcon from '../assets/nav-transcript.svg';
import useUserDetails from '../hooks/useUserDetails';
import SideBarNavigationLink from './SideBarNavigationLink';
import Loader from '../reusable/Loader';
import useUploadProgress from '../hooks/useUploadProgress';
import useDistrictRequiresTestScores from '../hooks/useDistrictRequiresTestScores';
import { ChildLink } from './SideBarNavigationLink';
import useSchools from '../hooks/useSchools';
import { UserDetails } from '../../interfaces/UserDetailsInterface';
import FocusGroupsIcon from '../assets/nav-focus-group-report.svg';
import SchoolReportsIcon from '../assets/nav-focus-group-report.svg';
import DeveloperIcon from '../assets/nav-developer.svg';
import ChatIcon from '../assets/nav-chat.svg';
import { FOCUS_GROUP_REPORT_POC_PATH } from '../focus-group-report-poc/generationService';
import { hasSelectedEoa } from '../district-selection/districtSelectionUtils';
import type GlobalState from '../../interfaces/GlobalStateInterface';
import type Schools from '../../interfaces/SchoolsInterface';

import './SideBarNavigation.css';

interface ChildLinkDataObject extends ChildLink {
  categoryName: string;
}

interface NavigationSection {
  sectionName?: string;
  items: NavigationLink[];
}

interface NavigationLink {
  label: string;
  path: string;
  children: ChildLink[];
  isExpandable: boolean;
  icon?: string;
  disabled: boolean;
  activePathPatterns: string[];
}

const generateDataQCSubLinks = (
  uploadProgress: GlobalState['uploadProgressSummary']['data'],
  dataQcSchool: string,
  schoolsUploadProgressByCategory: GlobalState['schoolsUploadProgressByCategory'],
  schools: Schools[],
  requiresTestScores: boolean
) => {
  if (uploadProgress.progress.length + uploadProgress.completed.length === 0) return [];

  let subLinks: ChildLinkDataObject[] = [
    {
      label: 'Transcripts',
      path: '/data-qc/transcripts',
      categoryName: DATA_CATEGORY_LABELS[fileTypes.STUDENT_TRANSCRIPTS],
      disabled: true,
      children: [],
    },
    {
      label: 'Test scores',
      path: '/data-qc/test-scores',
      categoryName: DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES],
      disabled: true,
      children: [],
    },
    {
      label: 'Course catalogs',
      path: '/data-qc/course-catalogs',
      categoryName: DATA_CATEGORY_LABELS[fileTypes.COURSE_CATALOG],
      disabled: true,
      children: [],
    },
  ];

  const visibleSubLinks = requiresTestScores
    ? subLinks
    : subLinks.filter((subLink) => subLink.categoryName !== DATA_CATEGORY_LABELS[fileTypes.TEST_SCORES]);

  return visibleSubLinks.map((subLink) => ({ ...subLink, disabled: false }));
};

const getNavLinks = (
  uploadProgress: GlobalState['uploadProgressSummary']['data'],
  dataQcSchool: string,
  schoolsUploadProgressByCategory: GlobalState['schoolsUploadProgressByCategory'],
  schools: Schools[],
  requiresTestScores: boolean
) => {
  let dataQcSubLinks = generateDataQCSubLinks(
    uploadProgress,
    dataQcSchool,
    schoolsUploadProgressByCategory,
    schools,
    requiresTestScores
  );
  return [
    {
      label: NAVIGATION_LINKS_DATA.OVERVIEW.label,
      path: NAVIGATION_LINKS_DATA.OVERVIEW.path,
      children: [],
      isExpandable: false,
      icon: OverviewIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.DASHBOARD.label,
      path: NAVIGATION_LINKS_DATA.DASHBOARD.path,
      children: [],
      isExpandable: true,
      icon: DashboardIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.DATA_UPLOAD.label,
      path: NAVIGATION_LINKS_DATA.DATA_UPLOAD.path,
      children: [],
      isExpandable: false,
      icon: DataUploadIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.COURSE_MAPPING.label,
      path: NAVIGATION_LINKS_DATA.COURSE_MAPPING.path,
      children: [],
      isExpandable: false,
      icon: CourseMappingIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.DATA_QC.label,
      path: NAVIGATION_LINKS_DATA.DATA_QC.path,
      children: dataQcSubLinks,
      isExpandable: dataQcSubLinks.length > 0,
      icon: DataQcIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.TRANSCRIPTS.label,
      path: NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
      children: [],
      isExpandable: false,
      icon: TranscriptsIcon,
      disabled: false,
      activePathPatterns: ['/transcripts'],
    },
    {
      label: NAVIGATION_LINKS_DATA.FOCUS_GROUPS.label,
      path: NAVIGATION_LINKS_DATA.FOCUS_GROUPS.path,
      children: [],
      isExpandable: false,
      icon: FocusGroupsIcon,
      disabled: false,
      activePathPatterns: [],
    },
    {
      label: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.label,
      path: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.path,
      children: [],
      isExpandable: false,
      icon: SchoolReportsIcon,
      disabled: false,
      activePathPatterns: [NAVIGATION_LINKS_DATA.SURVEY_RESPONSE_DASHBOARD.path],
    },
    {
      label: NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.label,
      path: NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
      children: [],
      isExpandable: false,
      icon: SchoolReportsIcon,
      disabled: false,
      activePathPatterns: [],
    },
  ];
};

const helperLinks = [
  { label: 'Help', path: '/help', children: [], isExpandable: false, icon: HelpIcon, activePathPatterns: [] },
];

const chatNavLink: NavigationLink = {
  label: NAVIGATION_LINKS_DATA.CHAT.label,
  path: NAVIGATION_LINKS_DATA.CHAT.path,
  children: [],
  isExpandable: false,
  icon: ChatIcon,
  disabled: false,
  activePathPatterns: [],
};

interface SideBarNavigationContentProps {
  classes: string[];
  isOpen: boolean;
  buttonAction: () => void;
  src: string;
  handleCloseSideDrawer?: () => void;
}

function SideBarNavigationContent({
  classes,
  isOpen,
  buttonAction,
  src,
  handleCloseSideDrawer,
}: SideBarNavigationContentProps) {
  const location = useLocation();

  const {
    userDetails: { isPending, error, data: userDetails },
  } = useUserDetails();

  const {
    uploadProgressSummary: { data: uploadProgress },
    schoolsUploadProgressByCategory,
  } = useUploadProgress();

  const {
    dataQcSchool,
    schools: { isPending: isSchoolsDataPending, error: schoolsError, data: schools },
  } = useSchools();

  const { requiresTestScores } = useDistrictRequiresTestScores();

  const classNames = classes.join(' ');

  const filterNavLinksByUserRole = (
    userData: UserDetails,
    schoolsData: Schools[]
  ): NavigationLink[] | { sections: NavigationSection[] } => {
    const navLinks = getNavLinks(
      uploadProgress,
      dataQcSchool,
      schoolsUploadProgressByCategory,
      schoolsData,
      requiresTestScores
    );

    switch (userData.role) {
      case USER_ROLES.SUPER_ADMIN:
        return [
          {
            label: NAVIGATION_LINKS_DATA.OVERVIEW.label,
            path: NAVIGATION_LINKS_DATA.OVERVIEW.path,
            children: [],
            isExpandable: false,
            icon: OverviewIcon,
            disabled: false,
            activePathPatterns: [],
          },
          {
            label: 'Developer Dashboard',
            path: '/developer-dashboard',
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [],
          },
          {
            label: NAVIGATION_LINKS_DATA.SURVEY_MANAGEMENT.label,
            path: NAVIGATION_LINKS_DATA.SURVEY_MANAGEMENT.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [],
          },
          {
            label: NAVIGATION_LINKS_DATA.CONFIGURATION.label,
            path: NAVIGATION_LINKS_DATA.CONFIGURATION.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.CONFIGURATION.path],
          },
          {
            label: NAVIGATION_LINKS_DATA.USER_MANAGEMENT.label,
            path: NAVIGATION_LINKS_DATA.USER_MANAGEMENT.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.USER_MANAGEMENT.path],
          },
          {
            label: NAVIGATION_LINKS_DATA.ACTIVITY_LOG.label,
            path: NAVIGATION_LINKS_DATA.ACTIVITY_LOG.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.ACTIVITY_LOG.path],
          },
          chatNavLink,
          {
            label: 'FG report POC',
            path: FOCUS_GROUP_REPORT_POC_PATH,
            children: [],
            isExpandable: false,
            icon: FocusGroupsIcon,
            disabled: false,
            activePathPatterns: [],
          },
        ];
      case USER_ROLES.DISTRICT_DATA_LEAD:
        return navLinks.filter(
          (navLink) =>
            ![
              NAVIGATION_LINKS_DATA.DASHBOARD.label,
              NAVIGATION_LINKS_DATA.TRANSCRIPTS.label,
              NAVIGATION_LINKS_DATA.FOCUS_GROUPS.label,
              NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.label,
              NAVIGATION_LINKS_DATA.SURVEY_REPORTS.label,
            ].includes(navLink.label)
        );
      case USER_ROLES.FACILITATOR:
        const facilitatorFilteredLinks = navLinks.filter((navLink) =>
          [NAVIGATION_LINKS_DATA.DASHBOARD.label, NAVIGATION_LINKS_DATA.FOCUS_GROUPS.label].includes(navLink.label)
        );

        const facilitatorDashboardLink = facilitatorFilteredLinks.find(
          (link) => link.label === NAVIGATION_LINKS_DATA.DASHBOARD.label
        );
        if (facilitatorDashboardLink) {
          facilitatorDashboardLink.children = [
            {
              label: 'Transcript selection',
              path: NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: 'Student voice for reports',
              path: '/student-voice-for-reports',
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.label,
              path: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
          ];
          facilitatorDashboardLink.activePathPatterns = [
            '/dashboard',
            NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
            '/transcripts',
            '/student-voice-for-reports',
            NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
          ];
        }

        return facilitatorFilteredLinks;
      case USER_ROLES.LEAD_FACILITATOR:
        const filteredLinks = navLinks.filter((navLink) =>
          [NAVIGATION_LINKS_DATA.DASHBOARD.label, NAVIGATION_LINKS_DATA.FOCUS_GROUPS.label].includes(navLink.label)
        );

        const dashboardLink = filteredLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.DASHBOARD.label);
        if (dashboardLink) {
          dashboardLink.children = [
            {
              label: 'District report tool',
              path: NAVIGATION_LINKS_DATA.CHAT.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.label,
              path: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: 'Focus group report',
              path: '/focus-group-reports-lead-facilitator',
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: 'Transcript selection',
              path: NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: 'Student voice for reports',
              path: '/student-voice-for-reports',
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: 'School report',
              path: NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
            {
              label: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.label,
              path: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
              categoryName: '',
              disabled: false,
              children: [],
            },
          ];
          dashboardLink.activePathPatterns = [
            '/dashboard',
            NAVIGATION_LINKS_DATA.CHAT.path,
            NAVIGATION_LINKS_DATA.SURVEY_REPORTS.path,
            NAVIGATION_LINKS_DATA.SURVEY_RESPONSE_DASHBOARD.path,
            '/focus-group-reports-lead-facilitator',
            NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
            '/transcripts',
            '/student-voice-for-reports',
            NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
            NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
          ];
        }

        return filteredLinks;
      case USER_ROLES.XQ_ADMIN:
        const xqAdminOverviewLink = navLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.OVERVIEW.label);
        const xqAdminDataUploadLink = navLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.DATA_UPLOAD.label);
        const xqAdminDataQcLink = navLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.DATA_QC.label);
        const xqAdminDashboardLink = navLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.DASHBOARD.label);
        const xqAdminFocusGroupsLink = navLinks.find((link) => link.label === NAVIGATION_LINKS_DATA.FOCUS_GROUPS.label);

        const districtDataLeadTasks: NavigationLink[] = [];
        const facilitatorTasks: NavigationLink[] = [];

        if (xqAdminOverviewLink) {
          districtDataLeadTasks.push({
            ...xqAdminOverviewLink,
            label: xqAdminOverviewLink.label,
            icon: OverviewIcon,
          });
        }

        if (xqAdminDataUploadLink) {
          districtDataLeadTasks.push({
            ...xqAdminDataUploadLink,
            label: 'Data upload',
          });
        }

        if (xqAdminDataQcLink) {
          districtDataLeadTasks.push({
            ...xqAdminDataQcLink,
            label: 'Data quality check',
          });
        }

        if (xqAdminDashboardLink) {
          const xqAdminDashboardWithChildren = {
            ...xqAdminDashboardLink,
            label: xqAdminDashboardLink.label,
            children: [
              {
                label: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.label,
                path: NAVIGATION_LINKS_DATA.SURVEY_REPORTS.path,
                categoryName: '',
                disabled: false,
                children: [],
              },
              {
                label: 'Focus group report',
                path: '/focus-group-reports-lead-facilitator',
                categoryName: '',
                disabled: false,
                children: [],
              },
              {
                label: 'Transcript selection',
                path: NAVIGATION_LINKS_DATA.TRANSCRIPTS.path,
                categoryName: '',
                disabled: false,
                children: [],
              },
              {
                label: 'Student voice for reports',
                path: '/student-voice-for-reports',
                categoryName: '',
                disabled: false,
                children: [],
              },
              {
                label: 'School report',
                path: NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
                categoryName: '',
                disabled: false,
                children: [],
              },
              {
                label: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.label,
                path: NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
                categoryName: '',
                disabled: false,
                children: [],
              },
            ],
            activePathPatterns: [
              '/dashboard',
              NAVIGATION_LINKS_DATA.SURVEY_REPORTS.path,
              NAVIGATION_LINKS_DATA.SURVEY_RESPONSE_DASHBOARD.path,
              '/focus-group-reports-lead-facilitator',
              '/transcripts',
              '/student-voice-for-reports',
              NAVIGATION_LINKS_DATA.SCHOOL_REPORTS.path,
              NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path,
            ],
          };
          facilitatorTasks.push(xqAdminDashboardWithChildren);
        }

        if (xqAdminFocusGroupsLink) {
          facilitatorTasks.push(xqAdminFocusGroupsLink);
        }

        const adminTasks: NavigationLink[] = [
          {
            label: NAVIGATION_LINKS_DATA.CONFIGURATION.label,
            path: NAVIGATION_LINKS_DATA.CONFIGURATION.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.CONFIGURATION.path],
          },
          {
            label: NAVIGATION_LINKS_DATA.USER_MANAGEMENT.label,
            path: NAVIGATION_LINKS_DATA.USER_MANAGEMENT.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.USER_MANAGEMENT.path],
          },
          {
            label: NAVIGATION_LINKS_DATA.ACTIVITY_LOG.label,
            path: NAVIGATION_LINKS_DATA.ACTIVITY_LOG.path,
            children: [],
            isExpandable: false,
            icon: DeveloperIcon,
            disabled: false,
            activePathPatterns: [NAVIGATION_LINKS_DATA.ACTIVITY_LOG.path],
          },
        ];

        return {
          sections: [
            { sectionName: 'District Data Lead tasks', items: districtDataLeadTasks },
            { sectionName: 'Facilitator tasks', items: facilitatorTasks },
            { sectionName: 'Admin tasks', items: adminTasks },
          ],
        };
      default:
        return [];
    }
  };

  return (
    <nav data-testid="sidebar" className={`sidebar ${classNames}`}>
      <ul className="sidebar-items">
        <li className="navigation-link-container arrow-back">
          <CustomButton variant="clear" buttonAction={buttonAction}>
            <img alt="open/close navigation menu" src={src} />
          </CustomButton>
        </li>

        <li>
          <CustomDivider size="small" orientation="horizontal" />
        </li>
        {isPending || isSchoolsDataPending ? <Loader /> : null}
        {error ? <p className="center-text">Missing user details!</p> : null}
        {schoolsError ? <p className="center-text">Missing schools data!</p> : null}
        {hasSelectedEoa(userDetails) && schools
          ? (() => {
              const navResult = filterNavLinksByUserRole(userDetails!, schools);
              if ('sections' in navResult) {
                return navResult.sections.map((section, sectionIndex) => (
                  <React.Fragment key={sectionIndex}>
                    {section.sectionName && (
                      <li className="navigation-section-header">
                        <span className="navigation-section-header-text">{section.sectionName}</span>
                      </li>
                    )}
                    {section.items.map((listItem) => (
                      <SideBarNavigationLink
                        key={listItem.label}
                        path={listItem.path}
                        icon={listItem.icon}
                        label={listItem.label}
                        children={listItem.children}
                        isExpandable={listItem.isExpandable}
                        currentPath={location.pathname}
                        isNavigationClosed={!isOpen}
                        disabled={listItem.disabled}
                        handleCloseSideDrawer={handleCloseSideDrawer}
                        activePathPatterns={listItem.activePathPatterns}
                      />
                    ))}
                    {sectionIndex < navResult.sections.length - 1 && (
                      <li>
                        <CustomDivider size="small" orientation="horizontal" className="navigation-section-divider" />
                      </li>
                    )}
                  </React.Fragment>
                ));
              } else {
                return navResult.map((listItem) => (
                  <SideBarNavigationLink
                    key={listItem.label}
                    path={listItem.path}
                    icon={listItem.icon}
                    label={listItem.label}
                    children={listItem.children}
                    isExpandable={listItem.isExpandable}
                    currentPath={location.pathname}
                    isNavigationClosed={!isOpen}
                    disabled={listItem.disabled}
                    handleCloseSideDrawer={handleCloseSideDrawer}
                    activePathPatterns={listItem.activePathPatterns}
                  />
                ));
              }
            })()
          : null}
        {hasSelectedEoa(userDetails) && userDetails!.role !== USER_ROLES.XQ_ADMIN && (
          <>
            <li>
              <CustomDivider size="small" orientation="horizontal" />
            </li>
            {helperLinks.map((listItem) => (
              <SideBarNavigationLink
                key={listItem.label}
                path={listItem.path}
                icon={listItem.icon}
                label={listItem.label}
                children={listItem.children}
                isExpandable={listItem.isExpandable}
                currentPath={location.pathname}
                isNavigationClosed={!isOpen}
                handleCloseSideDrawer={handleCloseSideDrawer}
                activePathPatterns={listItem.activePathPatterns}
              />
            ))}
          </>
        )}
      </ul>
    </nav>
  );
}

export default SideBarNavigationContent;
